/**
 * Registro inicial de usuario.
 *
 * Flujo:
 * - crea cuenta en Firebase Auth,
 * - guarda perfil y datos de suscripción base en Firestore,
 * - redirige a la pantalla de pago para completar la activación.
 */
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { db } from "../firebase"; // Import Firestore
import { doc, setDoc, serverTimestamp } from "firebase/firestore"; // Import Firestore methods
import "./Register.scss";
import { getMembershipPlan, membershipPlanPrices } from "./membershipPlanData";

const Register = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedPlan = location.state?.selectedPlan || "Basic";
  const selectedPlanData = getMembershipPlan(selectedPlan);

  // States for form inputs
  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [gender, setGender] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false); // Checkbox state
  const [error, setError] = useState("");

  const auth = getAuth();

  // Function to handle form submission and user registration
  const handleRegister = async (e) => {
    e.preventDefault();

    // Check if terms are accepted
    if (!acceptTerms) {
      setError("Debes aceptar los términos y condiciones para registrarte.");
      return;
    }

    try {
      // Create user with email and password
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Calcular fecha de renovación: 1 mes después de la fecha actual
      const renewalDate = new Date();
      renewalDate.setMonth(renewalDate.getMonth() + 1);

      // Save user data to Firestore, incluyendo la información de suscripción
      await setDoc(doc(db, "users", user.uid), {
        name,
        birthDate,
        gender,
        phone,
        email,
        subscriptionId: "", // Se actualizará después del pago
        subscriptionPlan: selectedPlan,
        subscriptionCost: membershipPlanPrices[selectedPlan] || selectedPlanData.price,
        subscriptionStartDate: serverTimestamp(),
        subscriptionRenewalDate: renewalDate,
        profileImage: "", // Placeholder para futura imagen de perfil
      });

      // Navigate to the payment page with selected plan
      navigate("/payment", { state: { selectedPlan } });
    } catch (error) {
      setError("Error al crear cuenta. Por favor, inténtalo de nuevo.");
    }
  };

  return (
    <div className="register-container">
      <div className="logo-register">
        <a href="/">
          <img src="/assets/images/logoBlanco.png" alt="Mentfull Logo" />
        </a>
      </div>

      <div className="register-background"></div>

      <div className="register-box">
        <h2>Regístrate en Mentfull</h2>
        <p>
          Has seleccionado el plan: <span>{selectedPlanData.name}</span> <br></br>
          <strong>Puedes cambiar tu plan en el siguiente paso*</strong>
        </p>

        <form onSubmit={handleRegister}>
          <label htmlFor="name">Nombre completo:</label>
          <input
            id="name"
            name="name"
            type="text"
            placeholder="Nombre completo"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          <div className="input-group">
            <div className="form-group pr-9">
              <label htmlFor="fechaNacimiento">Fecha de nacimiento:</label>
              <input
                name="fechaNacimiento"
                id="fechaNacimiento"
                type="date"
                placeholder="Fecha de nacimiento"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                required
              />
            </div>
            <div className="form-group pl-9">
              <label htmlFor="sexo">Sexo:</label>
              <select
                name="sexo"
                id="sexo"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                required
              >
                <option value="">Selecciona tu sexo</option>
                <option value="Male">Masculino</option>
                <option value="Female">Femenino</option>
                <option value="Other">Otro</option>
              </select>
            </div>
          </div>

          <div className="input-group">
            <div className="form-group pr-9">
              <label htmlFor="telefono">Teléfono:</label>
              <input
                name="telefono"
                id="telefono"
                type="tel"
                placeholder="Teléfono"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
            <div className="form-group pl-9">
              <label htmlFor="email">Email:</label>
              <input
                name="email"
                id="email"
                type="email"
                placeholder="Correo electrónico"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <label htmlFor="contrasena">Contraseña:</label>
          <input
            name="contrasena"
            id="contrasena"
            type="password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          {/* Checkbox for terms and conditions */}
          <label className="terms">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={() => setAcceptTerms(!acceptTerms)}
              required
            />
            <p>
              Acepto los <a href="/terms">términos y condiciones</a>
            </p>
          </label>

          <button type="submit">Crear cuenta</button>
        </form>
      </div>
      {error && <p className="error-message">{error}</p>}
    </div>
  );
};

export default Register;
